import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELDS = [
  ['trigger_timestamp', 'Trigger timestamp (UTC, ISO)', 'missed-tp-trigger', 'YYYY-MM-DDTHH:MM:SSZ'],
  ['fx_pair', 'FX pair (if conversion needed)', 'missed-tp-fx-pair', 'e.g. account+quote currency'],
  ['fx_rate', 'Historical FX rate', 'missed-tp-fx-rate', 'rate at trigger time'],
  ['expected_profit_loss', 'Expected realized P/L (account currency)', 'missed-tp-expected', 'must match the computed amount'],
  ['authorization_reference', 'Authorization reference', 'missed-tp-auth-ref', 'e.g. ticket id'],
];

export const MissedTpDialog = ({ position, onClose, onDone }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  if (!position) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/positions/${position.id}/missed-tp-settlement`,
        { ...form, settlement_price: position.take_profit });
      toast.success(`Settled: P/L ${r.data.profit_loss} ${r.data.account_currency}`);
      setForm({});
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Settlement failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!position} onOpenChange={(o) => { if (!o) { setForm({}); onClose(); } }}>
      <DialogContent data-testid="missed-tp-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Missed take-profit settlement</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-600" data-testid="missed-tp-original">
          {position.direction} {position.volume} {position.symbol} @ {position.open_price} · settles at original TP{' '}
          <span className="font-semibold">{position.take_profit}</span>
        </div>
        <div className="space-y-3">
          {FIELDS.map(([k, label, id, ph]) => (
            <div key={k}><Label>{label}</Label>
              <Input placeholder={ph} value={form[k] || ''} onChange={set(k)} data-testid={id} /></div>
          ))}
          <div><Label>Price evidence</Label>
            <Textarea value={form.evidence || ''} onChange={set('evidence')} data-testid="missed-tp-evidence" /></div>
          <div><Label>Reason</Label>
            <Textarea value={form.reason || ''} onChange={set('reason')} data-testid="missed-tp-reason" /></div>
          <div><Label>Type the position id to confirm: <span className="font-mono">{position.id}</span></Label>
            <Input value={form.confirm || ''} onChange={set('confirm')} data-testid="missed-tp-confirm" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setForm({}); onClose(); }} data-testid="missed-tp-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || form.confirm !== position.id} data-testid="missed-tp-submit">Settle at original TP</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
